import { createContext, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';

interface BreadcrumbOverride {
  /** Custom label to display for the current page breadcrumb */
  label?: string;
  /** Whether to hide intermediate route segments (e.g., "client" in "/engagement-letters/client/:id") */
  hideIntermediateSegments?: boolean;
}

interface BreadcrumbContextValue {
  override: BreadcrumbOverride | null;
  setOverride: (override: BreadcrumbOverride | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined);

export const BreadcrumbProvider = ({ children }: { children: ReactNode }) => {
  const [override, setOverride] = useState<BreadcrumbOverride | null>(null);

  const value = useMemo(() => ({ override, setOverride }), [override]);

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

export const useBreadcrumbOverride = () => {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error('useBreadcrumbOverride must be used within a BreadcrumbProvider');
  }
  return context;
};

/**
 * Hook to set a custom breadcrumb label for the current page.
 *
 * @example
 * ```tsx
 * const ClientPage = () => {
 *   const { data } = useClientData();
 *   useBreadcrumb({
 *     label: data?.clientName,
 *     hideIntermediateSegments: true
 *   });
 *   return <div>...</div>;
 * };
 * ```
 */
export const useBreadcrumb = (override: BreadcrumbOverride | null) => {
  const { setOverride } = useBreadcrumbOverride();

  // Serialize to a stable string so the effect only re-runs when values actually change
  const serialized = override ? JSON.stringify(override) : null;

  useLayoutEffect(() => {
    setOverride(serialized ? (JSON.parse(serialized) as BreadcrumbOverride) : null);

    return () => {
      setOverride(null);
    };
  }, [serialized, setOverride]);
};
